import React, { useEffect, useState } from 'react'
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Upload,
  Divider,
  Space,
  DatePicker,
  Table,
  Modal,
  Spin,
} from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import AdminHeader from '../CommonComponents/AdminHeader'
import AdminFooter from '../CommonComponents/AdminFooter'
import './StudentAdmissionForm.css'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import {
  getChildrenList,
  savePoliceDocuments,
  GetPoliceDocuments,
  UpdatePoliceDocuments,
  IMG_URL,
  DeletePoliceDocuments,
  GetPoliceDocumentsById,
} from '../APIComponents/AdminApi'
import axiosInstance from '../../Utils/axiosInstance'

const { Option } = Select

const PoliceDocumentForm = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find(
        (p) => p.page_name === 'F.I.R/Police Documents',
      )
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()

  const [childrenList, setChildrenList] = useState([])
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)

  const [showForm, setShowForm] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [editLoading, setEditLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [viewData, setViewData] = useState(null)
  const [documentsList, setDocumentsList] = useState([])

  const handleView = (record) => {
    setViewData(record)
    setViewModalVisible(true)
  }

  useEffect(() => {
    fetchChildren()
    fetchTableData()
  }, [])
  // ====================================

  const handleAddDocuments = () => {
    const values = form.getFieldsValue()

    const newRow = {
      key: Date.now(),
      firCopy: values.firCopy || [],
      policeReport: values.policeReport || [],
      medicalReport: values.medicalReport || [],
      victimStatement: values.victimStatement || [],
      additionalNotes: values.additionalNotes || '',
    }

    if (
      !newRow.firCopy.length &&
      !newRow.policeReport.length &&
      !newRow.medicalReport.length &&
      !newRow.victimStatement.length
    ) {
      toast.warning('Please upload at least one document')
      return
    }

    setDocumentsList((prev) => [...prev, newRow])

    //  CLEAR ALL DOCUMENT SECTION FIELDS
    form.setFieldsValue({
      firCopy: [],
      policeReport: [],
      medicalReport: [],
      victimStatement: [],
      additionalNotes: '',
    })
  }

  // ===============================
  const documentColumns = [
    {
      title: 'FIR Copy',
      render: (_, record) => record.firCopy?.[0]?.name || 'File',
    },
    {
      title: 'Police Report',
      render: (_, record) => record.policeReport?.[0]?.name || 'File',
    },
    {
      title: 'Medical Report',
      render: (_, record) => record.medicalReport?.[0]?.name || 'File',
    },
    {
      title: 'Victim Statement',
      render: (_, record) => record.victimStatement?.[0]?.name || 'File',
    },
    {
      title: 'Notes',
      dataIndex: 'additionalNotes',
      render: (text) => text || '-',
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => {
            setDocumentsList((prev) =>
              prev.filter((item) => item.key !== record.key),
            )
          }}
        />
      ),
    },
  ]

  // ================= FETCH DATA =================

  const fetchChildren = async () => {
    try {
      const res = await axiosInstance.get(getChildrenList)

      const filteredChildren = res.data.filter(
        (child) => ![1, 2, 3].includes(child.admission_type),
      )

      setChildrenList(filteredChildren)
    } catch (err) {
      toast.error('Failed to fetch children list')
    }
  }

  const fetchTableData = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(GetPoliceDocuments)
      setTableData(res.data.data)
    } catch (err) {
      toast.error('Failed to fetch table data')
    } finally {
      setTableLoading(false)
    }
  }

  // ================= ADD =================

  const handleAdd = () => {
    form.resetFields()
    setIsEditMode(false)
    setEditingId(null)
    setShowForm(true)
  }

  // ================= EDIT =================

  const handleEdit = (record) => {
    try {
      setEditLoading(true)
      setIsEditMode(true)
      setEditingId(record.reg_id)
      setShowForm(true)

      // ================= MAIN FORM =================
      form.setFieldsValue({
        childName: record.reg_id,
        // childName: {
        //   value: record.reg_id,
        //   label: record.student?.full_name,
        // },
        firNumber: record.fir_number,
        firDate: record.fir_date ? dayjs(record.fir_date) : null,
        policeStation: record.police_station_name,
        district: record.city,
        complaintDetails: record.details_of_complaint,
      })

      // ================= DOCUMENT LIST =================
      const mappedDocs =
        record.police_documents?.map((doc, index) => ({
          key: doc.id || index,

          firCopy: doc.fir_copy
            ? [
                {
                  uid: `fir-${index}`,
                  name: 'FIR Copy',
                  status: 'done',
                  url: IMG_URL + doc.fir_copy,
                },
              ]
            : [],

          policeReport: doc.police_report
            ? [
                {
                  uid: `police-${index}`,
                  name: 'Police Report',
                  status: 'done',
                  url: IMG_URL + doc.police_report,
                },
              ]
            : [],

          medicalReport: doc.medical_examination_report
            ? [
                {
                  uid: `medical-${index}`,
                  name: 'Medical Report',
                  status: 'done',
                  url: IMG_URL + doc.medical_examination_report,
                },
              ]
            : [],

          victimStatement: doc.statement_of_victim
            ? [
                {
                  uid: `victim-${index}`,
                  name: 'Victim Statement',
                  status: 'done',
                  url: IMG_URL + doc.statement_of_victim,
                },
              ]
            : [],

          additionalNotes: doc.additional_notes || '', // if backend sends
        })) || []

      // ✅ SET DOCUMENT LIST
      setDocumentsList(mappedDocs)

      form.setFieldsValue({
        firCopy: [],
        policeReport: [],
        medicalReport: [],
        victimStatement: [],
        additionalNotes: '',
      })
    } catch (error) {
      console.error(error)
    } finally {
      setEditLoading(false)
    }
  }

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      const res = await axiosInstance.delete(
        `${DeletePoliceDocuments}/${deleteId}`,
      )
      if (res.data === 'DELETED') {
        toast.success('Deleted successfully')
        setDeleteModal(false)
        fetchTableData()
      } else if (res.data === 'NOTFOUND') {
        toast.warn('Data not found!')
      }
    } catch (err) {
      toast.error('Delete failed')
    }
  }

  // ================= CANCEL =================

  const handleCancel = () => {
    form.resetFields()
    setIsEditMode(false)
    setEditingId(null)
    setShowForm(false)
    setDocumentsList([])
  }

  // ================= SUBMIT / UPDATE =================

  // const handleFinish = async (values) => {
  //   try {
  //     setLoading(true)

  //     const formData = new FormData()

  //     // ================= TEXT FIELDS =================
  //     formData.append(
  //       'reg_id',
  //       typeof values.childName === 'object'
  //         ? values.childName.value
  //         : values.childName,
  //     )
  //     formData.append('fir_number', values.firNumber)
  //     formData.append(
  //       'fir_date',
  //       values.firDate ? values.firDate.format('YYYY-MM-DD') : '',
  //     )
  //     formData.append('police_station_name', values.policeStation)
  //     formData.append('city', values.district)
  //     formData.append('details_of_complaint', values.complaintDetails)

  //     if (documentsList.length === 0) {
  //       const values = form.getFieldsValue()

  //       const tempDoc = {
  //         firCopy: values.firCopy || [],
  //         policeReport: values.policeReport || [],
  //         medicalReport: values.medicalReport || [],
  //         victimStatement: values.victimStatement || [],
  //       }

  //       documentsList.push(tempDoc)
  //     }
  //     // ================= FILE FIELDS =================
  //     documentsList.forEach((doc, index) => {
  //       const appendMultiFile = (fieldName, fileList) => {
  //         if (!fileList || fileList.length === 0) return

  //         const file = fileList[0]

  //         if (file.originFileObj) {
  //           formData.append(`${fieldName}[${index}]`, file.originFileObj)
  //         } else if (file.url) {
  //           const existingPath = file.url.replace(IMG_URL, '')
  //           formData.append(`${fieldName}[${index}]`, existingPath)
  //         }
  //       }

  //       appendMultiFile('fir_copy', doc.firCopy)
  //       appendMultiFile('police_report', doc.policeReport)
  //       appendMultiFile('medical_examination_report', doc.medicalReport)
  //       appendMultiFile('statement_of_victim', doc.victimStatement)
  //       formData.append(`additional_notes[${index}]`, doc.additionalNotes || '')
  //     })

  //     let response
  //     // ================= API CALL =================
  //     if (isEditMode) {
  //       response = await axiosInstance.post(savePoliceDocuments, formData)
  //       if (response.data == 'ALREADYEXIST') {
  //         toast.warn('Data already exist!')
  //       } else if (response.data == 'SAVED') {
  //         toast.success('Saved successfully')
  //         form.resetFields()
  //         setShowForm(false)
  //         setIsEditMode(false)
  //         setEditingId(null)
  //         fetchTableData()
  //         setDocumentsList([])
  //       }
  //     } else {
  //       response = await axiosInstance.post(savePoliceDocuments, formData)
  //       if (response.data == 'ALREADYEXIST') {
  //         toast.warn('Data already exist!')
  //       } else if (response.data == 'SAVED') {
  //         toast.success('Saved successfully')
  //         form.resetFields()
  //         setShowForm(false)
  //         setIsEditMode(false)
  //         setEditingId(null)
  //         fetchTableData()
  //         setDocumentsList([])
  //       }
  //     }
  //   } catch (err) {
  //     console.error(err)
  //     toast.error('Failed to submit')
  //   } finally {
  //     setLoading(false)
  //   }
  // }

  const handleFinish = async (values) => {
    try {
      setLoading(true)

      const formData = new FormData()

      // ================= TEXT FIELDS =================
      formData.append(
        'reg_id',
        typeof values.childName === 'object'
          ? values.childName.value
          : values.childName,
      )
      formData.append('fir_number', values.firNumber)
      formData.append(
        'fir_date',
        values.firDate ? values.firDate.format('YYYY-MM-DD') : '',
      )
      formData.append('police_station_name', values.policeStation)
      formData.append('city', values.district)
      formData.append('details_of_complaint', values.complaintDetails)

      // ================= HANDLE EMPTY DOCUMENT LIST =================
      let finalDocuments = [...documentsList]

      if (finalDocuments.length === 0) {
        const tempValues = form.getFieldsValue()

        finalDocuments.push({
          firCopy: tempValues.firCopy || [],
          policeReport: tempValues.policeReport || [],
          medicalReport: tempValues.medicalReport || [],
          victimStatement: tempValues.victimStatement || [],
          additionalNotes: tempValues.additionalNotes || '',
        })
      }

      // ================= FILE APPEND FUNCTION =================
      const appendMultiFile = async (fieldName, fileList, index) => {
        if (!fileList || fileList.length === 0) return

        const file = fileList[0]

        // ✅ New file (binary already)
        if (file.originFileObj) {
          formData.append(`${fieldName}[${index}]`, file.originFileObj)
        }

        // ✅ Existing file → convert URL to binary
        else if (file.url) {
          try {
            const response = await fetch(file.url)
            const blob = await response.blob()

            const fileName = file.name || `file-${index}`
            const fileObj = new File([blob], fileName, {
              type: blob.type || 'application/octet-stream',
            })

            formData.append(`${fieldName}[${index}]`, fileObj)
          } catch (err) {
            console.error(`Error converting ${fieldName}:`, err)
          }
        }
      }

      // ================= LOOP DOCUMENTS (IMPORTANT: async loop) =================
      for (let index = 0; index < finalDocuments.length; index++) {
        const doc = finalDocuments[index]

        await appendMultiFile('fir_copy', doc.firCopy, index)
        await appendMultiFile('police_report', doc.policeReport, index)
        await appendMultiFile(
          'medical_examination_report',
          doc.medicalReport,
          index,
        )
        await appendMultiFile('statement_of_victim', doc.victimStatement, index)

        formData.append(`additional_notes[${index}]`, doc.additionalNotes || '')
      }

      // ================= API CALL =================
      const response = await axiosInstance.post(savePoliceDocuments, formData)

      if (response.data === 'ALREADYEXIST') {
        toast.warn('Data already exist!')
      } else if (response.data === 'SAVED') {
        toast.success(
          isEditMode ? 'Updated successfully' : 'Saved successfully',
        )

        form.resetFields()
        setShowForm(false)
        setIsEditMode(false)
        setEditingId(null)
        setDocumentsList([])
        fetchTableData()
      }
    } catch (err) {
      console.error(err)
      toast.error('Failed to submit')
    } finally {
      setLoading(false)
    }
  }

  // ================= FILE VALIDATION =================

  const validateFile = (file) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
    ]

    const isAllowedType = allowedTypes.includes(file.type)

    if (!isAllowedType) {
      toast.error('Only JPG, PNG, and PDF files are allowed!')
      return Upload.LIST_IGNORE
    }

    const isLt3M = file.size / 1024 / 1024 < 3

    if (!isLt3M) {
      toast.warn('File must be smaller than 3MB!')
      return Upload.LIST_IGNORE
    }

    return false // prevent auto upload
  }

  // ================= TABLE COLUMNS =================

  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Student Name',
      render: (_, record) => record.student?.full_name || '-',
    },
    {
      title: 'FIR Number',
      dataIndex: 'fir_number',
    },
    {
      title: 'Police Station',
      dataIndex: 'police_station_name',
    },
    {
      title: 'City',
      dataIndex: 'city',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Action',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    title="View"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    title="Delete"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setDeleteId(record.reg_id)
                      setDeleteModal(true)
                    }}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  const handleStudentChange = async (value) => {
    try {
      setLoading(true)

      const res = await axiosInstance.get(`${GetPoliceDocumentsById}/${value}`)

      // ✅ IMPORTANT CHANGE
      const record = res?.data?.data?.[0]

      if (!record) {
        form.resetFields([
          'firNumber',
          'firDate',
          'policeStation',
          'district',
          'complaintDetails',
        ])
        setDocumentsList([])
        return
      }

      // ================= SET FORM =================
      form.setFieldsValue({
        firNumber: record.fir_number || '',
        firDate: record.fir_date ? dayjs(record.fir_date) : null,
        policeStation: record.police_station_name || '',
        district: record.city || '',
        complaintDetails: record.details_of_complaint || '',
      })

      // ================= SET DOCUMENTS =================
      const mappedDocs =
        record.police_documents?.map((doc, index) => ({
          key: doc.id || index,

          firCopy: doc.fir_copy
            ? [
                {
                  uid: `fir-${index}`,
                  name: doc.fir_copy,
                  status: 'done',
                  url: IMG_URL + doc.fir_copy,
                },
              ]
            : [],

          policeReport: doc.police_report
            ? [
                {
                  uid: `police-${index}`,
                  name: doc.police_report,
                  status: 'done',
                  url: IMG_URL + doc.police_report,
                },
              ]
            : [],

          medicalReport: doc.medical_examination_report
            ? [
                {
                  uid: `medical-${index}`,
                  name: doc.medical_examination_report,
                  status: 'done',
                  url: IMG_URL + doc.medical_examination_report,
                },
              ]
            : [],

          victimStatement: doc.statement_of_victim
            ? [
                {
                  uid: `victim-${index}`,
                  name: doc.statement_of_victim,
                  status: 'done',
                  url: IMG_URL + doc.statement_of_victim,
                },
              ]
            : [],

          additionalNotes: doc.additional_notes || '',
        })) || []

      setDocumentsList(mappedDocs)
    } catch (err) {
      console.error(err)
      toast.error('Failed to fetch student data')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <AdminHeader />

      <div className="student-form-section container-fluid">
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-3">
              FIR & Police Documents
            </h3>
          </div>

          {/* ================= TABLE SECTION ================= */}

          {!showForm && (
            <>
              <Row justify="space-between" className="mb-3">
                <Col xs={24} md={10} lg={14}></Col>
                <Col
                  xs={24}
                  md={6}
                  lg={4}
                  className="text-end pb-2 px-md-2 pb-md-0"
                >
                  {pagePermission.is_new == 1 && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAdd}
                    >
                      Add
                    </Button>
                  )}
                </Col>
                <Col xs={24} md={6} lg={6}>
                  <Input
                    placeholder="Search by name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Col>
              </Row>

              <Table
                dataSource={tableData.filter((item) => {
                  const search = searchText.toLowerCase()

                  return (
                    item.fir_number?.toLowerCase().includes(search) ||
                    item.city?.toLowerCase().includes(search) ||
                    item.student?.full_name?.toLowerCase().includes(search)
                  )
                })}
                columns={columns}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  onChange: (page, size) => {
                    setCurrentPage(page)
                    setPageSize(size)
                  },
                }}
                rowKey="id"
                bordered
                loading={tableLoading}
                className="shadow rounded custom-table p-0"
                style={{
                  overflowX: 'auto',
                  border: '1px solid #dfdfdf',
                  backgroundColor: 'white',
                }}
              />
            </>
          )}

          {/* ================= FORM SECTION ================= */}

          {showForm && (
            <Spin spinning={editLoading}>
              <div className="row student-form-container rounded shadow">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFinish}
                  autoComplete="off"
                >
                  {/*  Select Child */}
                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Select Children Name"
                        name="childName"
                        rules={[
                          { required: true, message: 'Please select a child' },
                        ]}
                      >
                        <Select
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option?.children
                              ?.toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          placeholder="Search by name / reg no"
                          disabled={isEditMode}
                          loading={loading}
                          optionLabelProp="label"
                          onChange={handleStudentChange}
                        >
                          {childrenList
                            .filter((child) => child.admission_type !== 1)
                            .map((child) => ({
                              label: `${child.fname} ${child.mname} ${child.lname} (Reg No: ${child.reg_no})`,
                              value: child.id,
                            }))
                            .map((opt) => (
                              <Option
                                key={opt.value}
                                value={opt.value}
                                label={opt.label}
                              >
                                {opt.label}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/*  FIR Details */}
                  <div className="divider-left-only">
                    <h5>FIR Information -</h5>
                  </div>

                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="FIR Number"
                        name="firNumber"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter FIR number',
                          },
                        ]}
                      >
                        <Input placeholder="Enter FIR number" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="FIR Date"
                        name="firDate"
                        rules={[
                          { required: true, message: 'Please select FIR date' },
                        ]}
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          format="DD-MM-YYYY"
                          placeholder="Select FIR date"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Police Station Name"
                        name="policeStation"
                        rules={[
                          {
                            required: true,
                            message: 'Enter police station name',
                          },
                        ]}
                      >
                        <Input placeholder="Enter police station name" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="City / District"
                        name="district"
                        rules={[
                          { required: true, message: 'Enter district or city' },
                        ]}
                      >
                        <Input placeholder="Enter district or city" />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <Form.Item
                        label="Details of Complaint / Incident"
                        name="complaintDetails"
                        rules={[
                          {
                            required: true,
                            message: 'Enter details of complaint',
                          },
                        ]}
                      >
                        <Input.TextArea
                          rows={4}
                          placeholder="Enter detailed description of incident or complaint"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/*  Police & Legal Documents */}
                  <div className="divider-left-only">
                    <h5>Police & Legal Documents -</h5>
                  </div>

                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label="FIR Copy"
                        name="firCopy"
                        valuePropName="fileList"
                        getValueFromEvent={(e) =>
                          Array.isArray(e) ? e : e?.fileList
                        }
                      >
                        <Upload
                          beforeUpload={validateFile}
                          accept=".jpg,.jpeg,.png,.pdf"
                          maxCount={1}
                          listType="picture"
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload FIR Copy (Max 3MB)
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>

                    {/* Police Report */}
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label="Police Report / Case Diary"
                        name="policeReport"
                        valuePropName="fileList"
                        getValueFromEvent={(e) =>
                          Array.isArray(e) ? e : e?.fileList
                        }
                      >
                        <Upload
                          beforeUpload={validateFile}
                          accept=".jpg,.jpeg,.png,.pdf"
                          maxCount={1}
                          listType="picture"
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload Police Report (Max 3MB)
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>

                    {/* Medical Report */}
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label="Medical Examination Report"
                        name="medicalReport"
                        valuePropName="fileList"
                        getValueFromEvent={(e) =>
                          Array.isArray(e) ? e : e?.fileList
                        }
                      >
                        <Upload
                          beforeUpload={validateFile}
                          accept=".jpg,.jpeg,.png,.pdf"
                          maxCount={1}
                          listType="picture"
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload Medical Report (Max 3MB)
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>

                    {/* Victim Statement */}
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label="Statement of Victim (if available)"
                        name="victimStatement"
                        valuePropName="fileList"
                        getValueFromEvent={(e) =>
                          Array.isArray(e) ? e : e?.fileList
                        }
                      >
                        <Upload
                          beforeUpload={validateFile}
                          accept=".jpg,.jpeg,.png,.pdf"
                          maxCount={1}
                          listType="picture"
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload Statement (Max 3MB)
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Additional Notes"
                        name="additionalNotes"
                      >
                        <Input.TextArea
                          rows={3}
                          placeholder="Any other relevant details or documents"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} className="text-end">
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddDocuments}
                      >
                        Add
                      </Button>
                    </Col>

                    <Col xs={24}>
                      <Table
                        dataSource={documentsList}
                        columns={documentColumns}
                        pagination={false}
                        rowKey="key"
                        className="shadow rounded custom-table p-0 mt-3"
                        style={{
                          overflowX: 'auto',
                          border: '1px solid #dfdfdf',
                          backgroundColor: 'white',
                        }}
                      />
                    </Col>
                  </Row>

                  <Divider />

                  {/*  Buttons */}
                  <Row justify="end" className="pb-5">
                    <Space>
                      <Button htmlType="reset" onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                      >
                        {isEditMode ? 'Update' : 'Submit'}
                      </Button>
                    </Space>
                  </Row>
                </Form>
              </div>
            </Spin>
          )}

          {/* ===== DELETE MODAL ===== */}
          <Modal
            title="Confirm Delete"
            open={deleteModal}
            onOk={handleDelete}
            onCancel={() => setDeleteModal(false)}
          >
            Are you sure you want to delete this record?
          </Modal>

          {/* ===== VIEW MODAL ===== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View FIR & Police Documents
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            width={900}
            style={{
              top: 20,
              paddingBottom: 50,
            }}
            centered
            bodyStyle={{
              maxHeight: '75vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingBottom: 10,
            }}
          >
            {viewData && (
              <div
                style={{
                  backgroundColor: '#f5f7fa',
                  padding: '20px 10px',
                  borderRadius: '8px',
                }}
              >
                {/* ================= BASIC INFO ================= */}

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Student Name :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.student?.full_name || '-'}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>FIR Number :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.fir_number || '-'}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>FIR Date :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.fir_date
                        ? dayjs(viewData.fir_date).format('DD-MM-YYYY')
                        : '-'}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Police Station :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.police_station_name || '-'}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>City / District :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">{viewData?.city || '-'}</div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Complaint Details :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.details_of_complaint || '-'}
                    </div>
                  </Col>
                </Row>

                <Divider />

                {/* ================= DOCUMENTS ================= */}

                <h4 style={{ marginBottom: 15 }}>📁 Uploaded Documents</h4>

                {viewData.police_documents?.length > 0 ? (
                  viewData.police_documents.map((doc, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: 20,
                        padding: 10,
                        background: '#fff',
                        borderRadius: 6,
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <h5 style={{ marginBottom: 10 }}>
                        Document #{index + 1}
                      </h5>

                      <Row gutter={16}>
                        {/* FIR COPY */}
                        {doc.fir_copy && (
                          <Col span={12} className="py-2">
                            <div className="file-card">
                              <strong>FIR Copy</strong>
                              <div style={{ marginTop: 10 }}>
                                <a
                                  href={IMG_URL + doc.fir_copy}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View File
                                </a>
                              </div>
                            </div>
                          </Col>
                        )}

                        {/* POLICE REPORT */}
                        {doc.police_report && (
                          <Col span={12} className="py-2">
                            <div className="file-card">
                              <strong>Police Report</strong>
                              <div style={{ marginTop: 10 }}>
                                <a
                                  href={IMG_URL + doc.police_report}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View File
                                </a>
                              </div>
                            </div>
                          </Col>
                        )}

                        {/* MEDICAL REPORT */}
                        {doc.medical_examination_report && (
                          <Col span={12} className="py-2">
                            <div className="file-card">
                              <strong>Medical Report</strong>
                              <div style={{ marginTop: 10 }}>
                                <a
                                  href={
                                    IMG_URL + doc.medical_examination_report
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View File
                                </a>
                              </div>
                            </div>
                          </Col>
                        )}

                        {/* VICTIM STATEMENT */}
                        {doc.statement_of_victim && (
                          <Col span={12} className="py-2">
                            <div className="file-card">
                              <strong>Victim Statement</strong>
                              <div style={{ marginTop: 10 }}>
                                <a
                                  href={IMG_URL + doc.statement_of_victim}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View File
                                </a>
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>

                      <Row className="mt-2">
                        <Col span={6}>
                          <strong>Additional Notes :</strong>
                        </Col>
                        <Col span={18}>
                          <div className="view-box">
                            {doc.additional_notes || '-'}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ))
                ) : (
                  <p>No documents available</p>
                )}
              </div>
            )}
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default PoliceDocumentForm
